import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Search, XCircle, CheckCircle2, Download, Loader2 } from "lucide-react";

export default function VerifyCertificate() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async (val) => {
    const num = (val ?? code).trim();
    if (!num) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.get(`/certificates/verify/${encodeURIComponent(num)}`);
      setResult(data);
    } catch (e) {
      setResult({ error: apiErr(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("code")) verify(params.get("code")); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="verify-page">
      <div className="text-center">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-gold/15 border border-gold/30 items-center justify-center mb-5"><ShieldCheck className="h-7 w-7 text-gold" /></div>
        <h1 className="font-display text-4xl font-extrabold text-white">Verify Certificate</h1>
        <p className="text-slate-300 mt-3">Enter the unique code on the certificate to confirm its authenticity.</p>
      </div>

      <div className="glass rounded-2xl p-6 mt-10">
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verify()}
            placeholder="CRT-2026-XXXXX-X" data-testid="certificate-verify-input"
            className="bg-surface-2 border-white/10 text-white font-mono placeholder:text-slate-500 h-12" />
          <Button className="btn-gold h-12 px-6" data-testid="certificate-verify-submit-button" onClick={() => verify()} disabled={loading}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          </Button>
        </div>

        {result?.valid && (
          <div className={`mt-6 border rounded-xl p-6 animate-fadeup ${result.expired ? "border-amber-500/40 bg-amber-500/10" : "border-emerald-500/30 bg-emerald-500/10"}`} data-testid="verify-result-valid">
            <div className={`flex items-center gap-2 font-semibold mb-4 ${result.expired ? "text-amber-400" : "text-emerald-400"}`}><CheckCircle2 className="h-5 w-5" /> {result.expired ? "Certificate found (expired — recertification required)" : "Valid certificate"}</div>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-white/10 pb-2"><dt className="text-slate-400">Holder</dt><dd className="text-white font-semibold">{result.name}</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-2"><dt className="text-slate-400">Credential</dt><dd className="text-white text-right">{result.credential} {result.acronym ? `(${result.acronym})` : ""}</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-2"><dt className="text-slate-400">Aligned to</dt><dd className="text-slate-200 text-right text-xs font-mono">{result.cert_line}</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-2"><dt className="text-slate-400">Score</dt><dd className="text-gold font-semibold">{result.final_score}</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-2"><dt className="text-slate-400">Issue date</dt><dd className="text-white">{result.issue_date}</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-2"><dt className="text-slate-400">Valid until</dt><dd className={result.expired ? "text-amber-400 font-semibold" : "text-white"}>{result.valid_until}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Certificate code</dt><dd className="text-white font-mono">{result.number}</dd></div>
            </dl>
            <a href={`${API}/certificates/${encodeURIComponent(result.number)}/download`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="w-full mt-5 border-gold/40 text-gold hover:bg-gold/10 bg-transparent" data-testid="download-certificate-btn">
                <Download className="h-4 w-4 mr-2" /> Download certificate (PDF)
              </Button>
            </a>
          </div>
        )}
        {result?.error && (
          <div className="mt-6 border border-rose-500/30 bg-rose-500/10 rounded-xl p-6 flex items-center gap-3 animate-fadeup" data-testid="verify-result-invalid">
            <XCircle className="h-6 w-6 text-rose-400" />
            <p className="text-rose-200 text-sm">{result.error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
